// src/pages/Auth/components/RegistrationForm.js

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import axiosInstance from 'utils/axiosConfig';
import { FAST_API_BASE_URL, LAMBDA_BASE_URL } from 'constants';
import { useNavigate } from 'react-router-dom';
import SoftSelect from 'components/SoftSelect';
import { LOADING_STATES, WEBSITE_PLATFORMS, STORAGE_KEYS } from '../constants';
import { StyledInput, StyledButton } from '../styles/Styles';
import { extractDomainName, getInstallSource } from '../utils';

const RegistrationForm = ({ email, isDarkMode, loadingState, onSubmit, showSnackbar }) => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    domainName: "",
    companyName: "",
    websitePlatform: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.domainName || !formData.websitePlatform || !formData.companyName) {
      showSnackbar("Please fill in all fields", "error");
      return;
    }

    const cleanDomain = extractDomainName(formData.domainName);

    if (cleanDomain === "abc.com") {
      showSnackbar("Please contact your admin to add user!", "warning");
      return;
    }

    await onSubmit({
      cleanDomain,
      websitePlatform: formData.websitePlatform.value,
      companyName: formData.companyName.trim()
    });
  };

  const isLoading = loadingState === LOADING_STATES.REGISTRATION;

  return (
    <form onSubmit={handleSubmit}>
      <Box>
        <Box mb={3}>
          <Typography 
            component="label" 
            htmlFor="domainName"
            sx={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Website Domain Name
          </Typography>
          <StyledInput
            id="domainName"
            name="domainName"
            placeholder="example.com"
            value={formData.domainName}
            onChange={(e) => setFormData(prev => ({...prev, domainName: e.target.value}))}
            required
            isDarkMode={isDarkMode}
            sx={{ padding: "12px 14px", fontSize: "14px" }}
          />
        </Box>

        <Box mb={3}>
          <Typography 
            component="label" 
            htmlFor="websitePlatform"
            sx={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Website Platform
          </Typography>
          <SoftSelect
            value={formData.websitePlatform}
            options={WEBSITE_PLATFORMS}
            onChange={(value) => setFormData(prev => ({...prev, websitePlatform: value}))}
            placeholder="Select Website Platform"
            required
            sx={{
              width: "100%",
              "& .MuiSelect-select": {
                padding: "12px 14px",
                fontSize: "14px",
                color: isDarkMode ? "#e2e8f0" : undefined,
              },
              "& .MuiOutlinedInput-root": {
                backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
                '& fieldset': {
                  borderColor: isDarkMode ? "#4a5568" : "#ccc",
                },
              },
            }}
          />
        </Box>

        <Box mb={3}>
          <Typography 
            component="label" 
            htmlFor="companyName"
            sx={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Company Name
          </Typography>
          <StyledInput
            id="companyName"
            name="companyName"
            placeholder="Sample Inc."
            value={formData.companyName}
            onChange={(e) => setFormData(prev => ({...prev, companyName: e.target.value}))}
            required
            isDarkMode={isDarkMode}
            sx={{ padding: "12px 14px", fontSize: "14px" }}
          />
        </Box>

        <StyledButton
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading}
          isDarkMode={isDarkMode}
        >
          {isLoading ? "Registering Domain..." : "Complete Registration"}
        </StyledButton>
      </Box>
    </form>
  );
};

export default React.memo(RegistrationForm);